/**
 * Styled status-filter dropdown (All / Active / Inactive / Demo).
 * Used by GHGOverviewPage and StatisticsOverviewPage.
 */

import React from 'react';
import { alpha, Box, Button, Menu, MenuItem, useTheme } from '@mui/material';
import { Check, KeyboardArrowDown } from '@mui/icons-material';
import { CompanyStatusFilter } from '../../services/api/companyApi';

export interface StatusFilterDropdownProps {
  value: CompanyStatusFilter | null;
  onChange: (value: CompanyStatusFilter | null) => void;
  /** Button height in px. Defaults to 54. */
  buttonHeight?: number;
  /** Gap between items inside the button. Defaults to 1.5. */
  buttonGap?: number;
  /** py for each MenuItem. Defaults to 1.25. */
  menuItemPy?: number;
}

const StatusFilterDropdown: React.FC<StatusFilterDropdownProps> = ({
  value,
  onChange,
  buttonHeight = 54,
  buttonGap = 1.5,
  menuItemPy = 1.25,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const statusOptions: Array<{ value: CompanyStatusFilter | null; label: string; dotColor: string }> = [
    { value: null,       label: 'All Statuses', dotColor: theme.palette.grey[400] },
    { value: 'active',   label: 'Active',       dotColor: theme.palette.success.main },
    { value: 'inactive', label: 'Inactive',     dotColor: theme.palette.error.main },
    { value: 'demo',     label: 'Demo',         dotColor: theme.palette.warning.main },
  ];

  const current = statusOptions.find((o) => o.value === value) ?? statusOptions[0];
  const menuOpen = Boolean(anchorEl);

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={menuOpen ? 'true' : undefined}
        endIcon={<KeyboardArrowDown />}
        sx={{
          height: buttonHeight,
          minWidth: 200,
          px: 2,
          borderRadius: 1,
          border: '2px solid',
          borderColor: value ? 'primary.main' : 'grey.200',
          background: value
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`
            : '#fff',
          color: 'text.primary',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          justifyContent: 'flex-start',
          gap: buttonGap,
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.light',
            background: value
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.16)} 100%)`
              : alpha(theme.palette.primary.main, 0.03),
          },
        }}
      >
        <Box
          component="span"
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: current.dotColor,
            flexShrink: 0,
          }}
        />
        <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8125rem', mr: 'auto' }}>
          Status:&nbsp;
          <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {current.label}
          </Box>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 240,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'grey.200',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)',
            },
          },
        }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <Box
          sx={{
            px: 1.75,
            py: 1.25,
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: 'text.secondary',
            borderBottom: '1px solid',
            borderColor: 'grey.100',
          }}
        >
          Filter by status
        </Box>
        {statusOptions.map((opt) => {
          const selected = value === opt.value;
          return (
            <MenuItem
              key={opt.label}
              selected={selected}
              onClick={() => {
                onChange(opt.value);
                setAnchorEl(null);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                py: menuItemPy,
                px: 1.75,
                fontSize: '0.875rem',
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  fontWeight: 500,
                },
                '&.Mui-selected:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: opt.dotColor,
                  flexShrink: 0,
                }}
              />
              <Box component="span" sx={{ flex: 1 }}>
                {opt.label}
              </Box>
              {selected && <Check fontSize="small" sx={{ color: 'primary.main' }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default StatusFilterDropdown;
